import React, { useState, useEffect } from "react";
import { ModalX } from "../../../components";
import { Button, Tab, Tabs, TabsHeader, Spinner } from "@material-tailwind/react";
import { BriefcaseIcon, BuildingIcon, FileTextIcon, ListIcon, ReceiptTextIcon, UserIcon } from "lucide-react";
import { AddNewLead, GetLeadById, UpdateLead, DeleteLead } from "../../../services/backend";
import { Notify, NotifyError } from "../../../utils/functions";
import {
  BUSINESS_LEADS,
  DEAL_STAGES,
  LEVELS,
  STATIC_USER_ROLES,
} from "../../../utils/enums";

// Importing sub-components
import BasicInfoTab from "./basicInfoTab";
import CompanyTab from "./companyTab";
import QualificationTab from "./qualificationTab";
import OpportunityTab from "./opertunityTab";
import GDPRTab from "./gdprTab";
import QuotationTab from "./quotationTab";
import LeadSummary from "./summaryTab";
import DesiredSeatsTab from "./desiredSeatsTab";
import DesiredOfficesTab from "./desiredOfficesTab";
import { useAddUser, useGetUsers } from "../../../custom-hooks/use-users";
import { useConfirm } from "../../../custom-hooks/confirm-dialog";
import { useNavigate } from "react-router-dom";
import LeadReadOnlyView from "./leadReadOnlyView";
import { URLS } from "../../../router-x/routes";



// Initial form data
const INITIAL_FORM_DATA = {
  // Core Information
  leadSource: '',
  leadStatus: DEAL_STAGES.NEW,

  // Contact Information
  fullName: '', // removed first and last name
  jobTitle: '',
  emailAddress: '',
  secondaryEmail: '',
  phoneNumber: '',
  countryCode: '',
  mobileNumber: '',
  linkedinProfile: '',
  otherSocialMedia: {},
  customerId: null,
  cnic: '',
  connectionDate: '',

  // Company Information
  companyName: '',
  companyWebsite: '',
  industry: '',
  companySize: '',
  annualRevenue: '',
  companyAddress: {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: ''
  },
  companyPhone: '',

  // Qualification Data
  budgetRange: '',
  decisionTimeline: '',
  isDecisionMaker: false,
  decisionProcess: '',
  painPoints: [],
  requirements: [],
  solutionInterest: [],
  competitorConsideration: [],

  // Engagement History
  engagementHistory: [],
  firstContactDate: '',
  lastContactDate: '',

  // Lead Scoring
  score: 0,
  engagementLevel: LEVELS.MEDIUM,
  purchaseIntent: '',
  conversionProbability: 0,

  // Assignment Information
  desiredSeatsCount: 0,
  desiredSeats: [],
  desiredOfficesCount: 0,
  desiredOffices: [],
  assignedSalesRep: null,
  salesTeam: '',
  assignmentDate: '',
  territory: '',

  // Follow-up Planning
  nextAction: '',
  actionDueDate: '',
  followUpNotes: '',
  followUpPriority: LEVELS.MEDIUM,

  // Opportunity Details
  opportunityDetails: {
    opportunityId: '',
    estimatedDealSize: 0,
    productsInterested: [],
    proposalStatus: '',
    negotiationStage: '',
    projectedCloseDate: ''
  },

  // Additional Information
  tags: [],
  customFields: {},
  notes: '',
  attachments: [],

  // GDPR/Privacy Compliance
  communicationConsent: false,
  consentDate: '',
  communicationPreferences: {
    email: false,
    phone: false,
    sms: false,
    directMail: false,
    whatsapp: false
  },
  dataRetentionDate: '',
  dataUsagePermissions: []
};



// Modal tabs
const buildModalTabs = ({ showQuotation }) => {
  const tabs = [
    { label: 'Basic Info', value: 'basic', icon: <UserIcon size={12} /> },
    { label: 'Company', value: 'company', icon: <BuildingIcon size={12} /> },
    { label: 'Seats', value: 'seats', icon: <BuildingIcon size={12} /> },
    { label: 'Offices', value: 'offices', icon: <BuildingIcon size={12} /> },
    { label: 'Qualification', value: 'qualification', icon: <ListIcon size={12} /> },
    { label: 'Opportunity', value: 'opportunity', icon: <BriefcaseIcon size={12} /> },
    { label: 'GDPR', value: 'gdpr', icon: <FileTextIcon size={12} /> },
  ];

  if (showQuotation) {
    tabs.push({ label: 'Quotation', value: 'quotation', icon: <ReceiptTextIcon size={12} /> });
  }

  return tabs;
};

const LeadFormModal = ({ isOpen, onClose, leadId, isUpdating, onSuccess, readOnly }) => {

  // State
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  // const [salesReps, setSalesReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  // Last persisted lead status (drives quotation tab visibility — not affected by unsaved form edits)

  // Arrays management
  const [newPainPoint, setNewPainPoint] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newSolutionInterest, setNewSolutionInterest] = useState('');
  const [newCompetitor, setNewCompetitor] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newProduct, setNewProduct] = useState('');

  const { confirm, ConfirmationDialog } = useConfirm();
  const navigate = useNavigate();
  const addCustomerMutation = useAddUser();


  const {
    data: salesReps,
    // isLoading: isUsersLoading, 
    // error: usersError 
  } = useGetUsers({
    role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE, STATIC_USER_ROLES.STAFF],
    _limit: 1000
  });


  // Fetch lead data when the modal opens for editing
  useEffect(() => {
    const fetchLead = async () => {
      if (isUpdating && leadId && isOpen) {
        setLoading(true);
        try {
          const response = await GetLeadById(leadId);
          const lead = response.data.lead;
          setFormData({
            ...lead,
            assignedSalesRep: lead.assignedSalesRep?._id || null,
          });
        } catch (error) {
          console.error('Error fetching lead details', error);
          NotifyError('Failed to fetch lead details', error);
        } finally {
          setLoading(false);
        }
      }
    };

    if (isOpen) {
      if (isUpdating && leadId) {
        fetchLead();
      } else {
        // Reset form data for new lead
        setFormData({
          ...INITIAL_FORM_DATA,
          leadStatus: DEAL_STAGES.NEW,
          engagementLevel: LEVELS.MEDIUM,
          followUpPriority: LEVELS.MEDIUM,
          opportunityDetails: {
            ...INITIAL_FORM_DATA.opportunityDetails,
            proposalStatus: BUSINESS_LEADS.NEW,
          }
        });
      }

      // fetchSalesReps();
      setActiveTab('basic');
    }
  }, [isOpen, isUpdating, leadId]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle nested object input change
  const handleNestedInputChange = (parentKey, childKey, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBoxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleNestedCheckBoxChange = (parentKey, childKey, checked) => {
    setFormData((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: checked
      }
    }));
  };

  // Array field handlers
  const handleAddPainPoint = () => {
    if (newPainPoint.trim()) {
      setFormData(prev => ({
        ...prev,
        painPoints: [...(prev.painPoints || []), newPainPoint.trim()]
      }));
      setNewPainPoint('');
    }
  };

  const handleRemovePainPoint = (index) => {
    setFormData(prev => ({
      ...prev,
      painPoints: (prev.painPoints || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddRequirement = () => {
    if (newRequirement.trim()) {
      setFormData(prev => ({
        ...prev,
        requirements: [...(prev.requirements || []), newRequirement.trim()]
      }));
      setNewRequirement('');
    }
  };

  const handleRemoveRequirement = (index) => {
    setFormData(prev => ({
      ...prev,
      requirements: (prev.requirements || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddSolutionInterest = () => {
    if (newSolutionInterest.trim()) {
      setFormData(prev => ({
        ...prev,
        solutionInterest: [...(prev.solutionInterest || []), newSolutionInterest.trim()]
      }));
      setNewSolutionInterest('');
    }
  };

  const handleRemoveSolutionInterest = (index) => {
    setFormData(prev => ({
      ...prev,
      solutionInterest: (prev.solutionInterest || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddCompetitor = () => {
    if (newCompetitor.trim()) {
      setFormData(prev => ({
        ...prev,
        competitorConsideration: [...(prev.competitorConsideration || []), newCompetitor.trim()]
      }));
      setNewCompetitor('');
    }
  };

  const handleRemoveCompetitor = (index) => {
    setFormData(prev => ({
      ...prev,
      competitorConsideration: (prev.competitorConsideration || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddProduct = () => {
    if (newProduct.trim()) {
      setFormData(prev => ({
        ...prev,
        opportunityDetails: {
          ...prev.opportunityDetails,
          productsInterested: [...(prev.opportunityDetails?.productsInterested || []), newProduct.trim()]
        }
      }));
      setNewProduct('');
    }
  };

  const handleRemoveProduct = (index) => {
    setFormData(prev => ({
      ...prev,
      opportunityDetails: {
        ...prev.opportunityDetails,
        productsInterested: (prev.opportunityDetails?.productsInterested || []).filter((_, i) => i !== index)
      }
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (index) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter((_, i) => i !== index)
    }));
  };

  const handleAddAttachment = async (attachment) => {
    const updatedAttachments = [...(formData.attachments || []), attachment];
    
    setFormData(prev => ({
      ...prev,
      attachments: updatedAttachments
    }));

    if (isUpdating && leadId) {
      try {
        await UpdateLead(leadId, { attachments: updatedAttachments });
        // We do not call onSuccess() here aggressively to avoid unnecessary table re-fetches while user is typing in other fields,
        // but the attachment is saved to the DB!
      } catch (error) {
        console.error('Failed to auto-save attachment', error);
        NotifyError('Failed to save attachment to database', error);
      }
    }
  };

  const handleRemoveAttachment = async (index) => {
    const isConfirmed = await confirm({
      title: 'Remove Attachment',
      message: 'Are you sure you want to delete this attachment? This action cannot be undone.',
      confirmColor: 'red',
      confirmText: 'Yes, Delete',
      cancelText: 'Cancel',
    });

    if (!isConfirmed) return;

    const updatedAttachments = (formData.attachments || []).filter((_, i) => i !== index);
    
    setFormData(prev => ({
      ...prev,
      attachments: updatedAttachments
    }));

    if (isUpdating && leadId) {
      try {
        await UpdateLead(leadId, { attachments: updatedAttachments });
        Notify('Attachment removal saved automatically', 'success');
      } catch (error) {
        console.error('Failed to auto-save attachment removal', error);
        NotifyError('Failed to update database after removing attachment', error);
      }
    }
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...formData };

    // Lead source validation
    if (!payload.leadSource) {
      NotifyError("Please select a Lead Source");
      return;
    }

    // Phone number validation
    if (!payload.phoneNumber) {
      NotifyError("Please enter a Phone Number");
      return;
    }

    // Phone number formatting
    if (payload.phoneNumber) {
      // If user naturally typed '0' at the start of their number (e.g., 0300...), remove it
      if (payload.phoneNumber.startsWith('0')) {
        payload.phoneNumber = payload.phoneNumber.substring(1);
      }

      const countryCode = payload.countryCode || '+92';
      payload.countryCode = countryCode;
      const fullPhone = countryCode + payload.phoneNumber;

      // specific check for Pakistan (+92)
      if (countryCode === '+92' && payload.phoneNumber.length !== 10) {
        NotifyError("For Pakistan (+92), the phone number must be 10 digits");
        return;
      }

      // General validation: must start with + followed by digits (usually 7 to 15 digits globally)
      if (!/^\+\d{7,15}$/.test(fullPhone)) {
        NotifyError("Please enter a valid phone number containing only numeric digits");
        return;
      }
    }

    try {
      if (isUpdating) {
        if (formData.leadStatus === DEAL_STAGES.WON) {
          const isConfirmed = await confirm({
            title: 'Convert Lead to Customer Confirmation',
            message: <div className="text-sm">
              You have selected lead status as <span className="font-bold text-green-500">Won</span>.
              This action will convert the lead to a customer.
              <div className="mt-2">
                Are you sure you want to proceed?
              </div>
            </div>,
            confirmColor: 'green',
            confirmText: 'Yes Proceed',
            cancelText: 'No Just Update Lead',
          });
          if (isConfirmed) {
            setLoading(true);
            // ** Add new customer logic
            const newUser = {
              email: payload.emailAddress,
              name: payload.fullName,
              cnic: payload.cnic,
              phone: payload.countryCode + payload.phoneNumber
            };
            const response = await addCustomerMutation.mutateAsync(newUser);
            const customerId = response.data.user._id;
            await UpdateLead(leadId, { ...payload, customerId });
            Notify('Lead updated successfully', 'success');

            const consentForContract = await confirm({
              title: 'Contract for Customer Confirmation',
              message: 'Lets Proceed to create a contract for this customer',
              confirmColor: 'green',
              confirmText: 'Confirm',
              cancelText: 'Cancel',
            });
            if (consentForContract) {
              navigate(`${URLS.ADMIN.CUSTOMER}/${customerId}`);
            }
            onSuccess();
            onClose();
          }
          else {
            await UpdateLead(leadId, { ...payload, customerId: null });
            Notify('Lead updated successfully', 'success');
            onSuccess();
            onClose();
          }
        } else {
          setLoading(true);
          await UpdateLead(leadId, payload);
          Notify('Lead updated successfully', 'success');
          onSuccess();
          onClose();
        }
      } else {
        if (formData.leadStatus === DEAL_STAGES.WON) {
          const isConfirmed = await confirm({
            title: 'Convert Lead to Customer Confirmation',
            message: <div className="text-sm">
              You have selected lead status as <span className="font-bold text-green-500">Won</span>.
              This action will convert the lead to a customer.
              <div className="mt-2">
                Are you sure you want to proceed?
              </div>
            </div>,
            confirmColor: 'green',
            confirmText: 'Yes Proceed',
            cancelText: 'No Just Update Lead',
          });
          if (isConfirmed) {
            setLoading(true);
            // ** Add new customer logic
            const newUser = {
              email: payload.emailAddress,
              name: payload.fullName,
              cnic: payload.cnic,
              phone: payload.countryCode + payload.phoneNumber
            };
            const response = await addCustomerMutation.mutateAsync(newUser);
            const customerId = response.data.user._id;
            await AddNewLead({ ...payload, customerId });
            Notify('Lead updated successfully', 'success');

            const consentForContract = await confirm({
              title: 'Contract for Customer Confirmation',
              message: 'Lets Proceed to create a contract for this customer',
              confirmColor: 'green',
              confirmText: 'Confirm',
              cancelText: 'Cancel',
            });
            if (consentForContract) {
              navigate(`${URLS.ADMIN.CUSTOMER}/${customerId}`);
            }
            onSuccess();
            onClose();
          }
          else {
            await AddNewLead({ ...payload, customerId: null });
            Notify('Lead updated successfully', 'success');
            onSuccess();
            onClose();
          }
        }
        else {
          setLoading(true);
          await AddNewLead(payload);
          Notify('Lead added successfully', 'success');
          onSuccess();
          onClose();
        }
      }

    } catch (error) {
      console.error('Error saving lead', error);
      NotifyError('Failed to save lead', error);
    } finally {
      setLoading(false);
    }
  };

  // Delete lead
  const handleDeleteLead = async () => {
    const isConfirmed = await confirm({
      title: 'Delete Lead',
      message: 'Are you sure you want to delete this lead?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmBtnColor: 'red',
    });
    if (isConfirmed) {
      try {
        await DeleteLead(leadId);
        Notify('Lead deleted successfully', 'success');
        onSuccess();
        onClose();
      } catch (error) {
        console.error('Error deleting lead', error);
        NotifyError('Failed to delete lead', error);
      }
    }
  };

  // Gather all handlers and state for child components
  const handlers = {
    handleInputChange,
    handleNestedInputChange,
    handleSelectChange,
    handleCheckBoxChange,
    handleNestedCheckBoxChange,
    handleAddPainPoint,
    handleRemovePainPoint,
    handleAddRequirement,
    handleRemoveRequirement,
    handleAddSolutionInterest,
    handleRemoveSolutionInterest,
    handleAddCompetitor,
    handleRemoveCompetitor,
    handleAddProduct,
    handleRemoveProduct,
    handleAddTag,
    handleRemoveTag,
    handleAddAttachment,
    handleRemoveAttachment
  };

  const arrayState = {
    newPainPoint,
    setNewPainPoint,
    newRequirement,
    setNewRequirement,
    newSolutionInterest,
    setNewSolutionInterest,
    newCompetitor,
    setNewCompetitor,
    newTag,
    setNewTag,
    newProduct,
    setNewProduct
  };

  const showQuotation = true;
  const modalTabs = buildModalTabs({ showQuotation });

  useEffect(() => {
    if (activeTab === 'quotation' && !showQuotation) {
      setActiveTab('basic');
    }
  }, [activeTab, showQuotation]);

  if (readOnly) {
    return (
      <ModalX
        isOpen={isOpen}
        onClose={onClose}
        title="Lead Summary"
        maxWidth="xxxl"
      >
        {
          loading ? (
            <div className="flex items-center justify-center min-h-[700px]">
              <Spinner />
            </div>
          ) : (
            <LeadReadOnlyView lead={formData} />
          )
        }
      </ModalX>
    )
  }

  return (
    <ModalX
      isOpen={isOpen}
      onClose={onClose}
      title={readOnly ? 'Lead Summary' : (isUpdating ? "Update Lead" : "Add New Lead")}
      maxWidth={readOnly ? "xxl" : "xxxl"}
    >
      <div className="relative min-h-[700px]">
        {loading && (
          <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm rounded-lg">
            <Spinner />
          </div>
        )}
        <div className={`transition-opacity duration-300 ${loading ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
          <div className="flex gap-5 flex-col md:flex-row min-h-[700px]">
            {
              !readOnly &&
              <div className="w-full md:w-8/12">
                <form onSubmit={handleFormSubmit}>
                  <div className={`flex flex-col justify-between min-h-[700px]`}>
                    <div className="space-y-2">
                      <div className="pb-3">
                        <Tabs key={leadId || 'new'} value={activeTab} onChange={(value) => setActiveTab(value)}>
                          <TabsHeader className="rounded-full" indicatorProps={{ className: 'rounded-full' }}>
                            {modalTabs.map((tab, index) => (
                              <Tab key={index} className="rounded-full font-thin text-xs" value={tab.value} onClick={() => setActiveTab(tab.value)}>
                                <span className="hidden md:block">{tab.label}</span>
                                <span className="block md:hidden">{tab.icon}</span>
                              </Tab>
                            ))}
                          </TabsHeader>
                        </Tabs>
                        <div className="min-h-[500px] mt-4">
                          {/* Basic Info Tab */}
                          {activeTab === 'basic' && (
                            <BasicInfoTab
                              formData={formData}
                              handlers={handlers}
                              salesReps={salesReps?.users}
                            />
                          )}

                          {/* Company Tab */}
                          {activeTab === 'company' && (
                            <CompanyTab
                              formData={formData}
                              handlers={handlers}
                            />
                          )}

                          {/* Seats Tab */}
                          {activeTab === 'seats' && (
                            <DesiredSeatsTab
                              formData={formData}
                              handlers={handlers}
                            />
                          )}

                          {/* Offices Tab */}
                          {activeTab === 'offices' && (
                            <DesiredOfficesTab
                              formData={formData}
                              handlers={handlers}
                            />
                          )}

                          {/* Qualification Tab */}
                          {activeTab === 'qualification' && (
                            <QualificationTab
                              formData={formData}
                              handlers={handlers}
                              arrayState={arrayState}
                            />
                          )}

                          {/* Opportunity Tab */}
                          {activeTab === 'opportunity' && (
                            <OpportunityTab
                              formData={formData}
                              handlers={handlers}
                              arrayState={arrayState}
                            />
                          )}

                          {/* GDPR Tab */}
                          {activeTab === 'gdpr' && (
                            <GDPRTab
                              formData={formData}
                              handlers={handlers}
                              arrayState={arrayState}
                            />
                          )}

                          {/* Quotation Tab */}
                          {activeTab === 'quotation' && showQuotation && (
                            <QuotationTab
                              lead={formData}
                              leadId={leadId}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {activeTab !== 'quotation' && (
                      <div>
                        <hr />
                        <div className="mt-4 flex justify-end space-x-2">
                          <Button
                            onClick={onClose}
                            className="rounded-full"
                            variant="text"
                          >
                            Cancel
                          </Button>
                          {isUpdating && (
                            <Button
                              onClick={handleDeleteLead}
                              className="rounded-full hidden"
                              color="red"
                              type="button"
                            >
                              Delete Lead
                            </Button>
                          )}
                          <Button
                            type="submit"
                            className="rounded-full"
                            disabled={loading}
                          >
                            {isUpdating ? "Update Lead" : "Add Lead"}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            }
            {/* Summary/Preview Panel */}
            <div className={`${readOnly ? 'w-full' : 'w-full md:w-4/12'} hidden md:block border rounded-3xl p-4`}>
              <LeadSummary formData={formData} />
            </div>
          </div>
          {/* <CustomerFormModal
              isOpen={customerFormModalOpen}
              onClose={() => setCustomerFormModalOpen(false)}
              defaultData={{
                name: formData.firstName + ' ' + formData.lastName,
                email: formData.emailAddress,
                phone: formData.phoneNumber,
                cnic: '',
                role: STATIC_USER_ROLES.USER,
              }}
            /> */}
        </div>
      </div>
      <ConfirmationDialog />
    </ModalX>
  );
};

export default LeadFormModal;